import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lightbulb, RefreshCw, Sparkles, CheckCircle2, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

// ============================================================
// AI 经营建议区（v6.3.22）
//   - 零 token 规则引擎：读取官网统计 + 评论反馈 + 经营决策规则库
//     → 按规则库 R01-R18 场景匹配，输出 3-5 条可执行经营建议
//   - 可选「AI 深化分析」按钮（用户点击才调 LLM，控制 token）
//   数据源：/stats?key=xeaos2026&days=7（官网访问） / feedback-list（评论）
//           /vault-api 经营决策规则库.md（决策规则）
// ============================================================

interface AdviceItem {
  id: string;
  rule: string;          // 命中的规则编号（如 R08）
  title: string;         // 建议标题
  action: string;        // 可执行行动指令
  basis: string;         // 数据依据
  done?: boolean;
}

const STATS_KEY = "xeaos2026";

// P1-① Ontology「企业地图」优先注入：6 个实体索引作为提示顶层锚点
// 让 AI「先看地图，再给建议」，从根上降幻觉（RAG 检索侧已由 VaultBridge 覆盖，此处补规则/AI 侧）
const ONTO_FILES = [
  "09_Knowledge（知识中心）/Ontology/客户-index.md",
  "09_Knowledge（知识中心）/Ontology/项目-index.md",
  "09_Knowledge（知识中心）/Ontology/承诺-index.md",
  "09_Knowledge（知识中心）/Ontology/信源-index.md",
  "09_Knowledge（知识中心）/Ontology/内容资产-index.md",
  "09_Knowledge（知识中心）/Ontology/供应商-index.md",
];

export default function BusinessAdvicePanel() {
  const [advice, setAdvice] = useState<AdviceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepResult, setDeepResult] = useState("");

  const loadAdvice = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // 1) 官网统计（近 7 天）
      let stats: any = null;
      try {
        const r = await fetch(`/stats?key=${STATS_KEY}&days=7`).catch(() => null);
        if (r && r.ok) stats = await r.json();
      } catch { /* 静默 */ }

      // 2) 评论反馈
      let feedback: any = null;
      try {
        const r = await fetch(`/feedback-list?key=${STATS_KEY}`).catch(() => null);
        if (r && r.ok) feedback = await r.json();
      } catch { /* 静默 */ }

      // 3) 经营决策规则库（从 Vault 读取）
      let rulesText = "";
      try {
        const r = await fetch(
          `/vault-api/read?path=${encodeURIComponent("10_Operations（运营层）/经营决策规则库.md")}`
        ).catch(() => null);
        if (r && r.ok) {
          const d = await r.json();
          if (d.success) rulesText = String(d.content || "");
        }
      } catch { /* 静默 */ }

      const items: AdviceItem[] = [];
      const todayVisits = stats?.todayVisits ?? 0;
      const weekVisits = stats?.weekVisits ?? stats?.totalVisits ?? 0;
      const topPages: string[] = Array.isArray(stats?.topPages) ? stats.topPages.map((p: any) => p?.path ?? p?.page ?? "") : [];
      const fbTotal = Array.isArray(feedback?.items) ? feedback.items.length : 0;
      const fbNegative = Array.isArray(feedback?.items)
        ? feedback.items.filter((c: any) => Number(c?.score ?? 0) <= 2).length
        : 0;
      const hasRules = rulesText.length > 100;

      // ---- 规则引擎：按数据触发场景 ----
      // R09 官网工具是引流入口：看 TOP 页面
      if (topPages.length > 0) {
        items.push({
          id: `adv-${Date.now()}-1`,
          rule: "R09",
          title: `本周访问集中在「${(topPages[0] || "首页").replace(/^\//, "")}」`,
          action: "把访问最高的工具页作为转化主阵地：在该页重点运营评论区 + 挂上咨询/付费入口，引导到私域。",
          basis: `官网近 7 天页面 TOP1：${topPages[0] || "无"}，共 ${weekVisits} 次访问`,
        });
      } else {
        items.push({
          id: `adv-${Date.now()}-1`,
          rule: "R09",
          title: "官网访问数据暂未采集到",
          action: "确认官网各页面已加载 track.js 埋点（首页/工具页），明天再看统计；若一直为空则检查服务器 stats 文件。",
          basis: "stats 接口未返回 topPages",
        });
      }

      // R06 内容用创始人口吻：评论负面或低分提示内容问题
      if (fbNegative > 0) {
        items.push({
          id: `adv-${Date.now()}-2`,
          rule: "R06",
          title: `收到 ${fbNegative} 条低分评论（≤2星）`,
          action: "逐个查看低分评论内容，定位是「工具不准」还是「体验问题」，回应用户并记录改进点；同时检查近期生成内容口吻是否跑偏。",
          basis: `评论区共 ${fbTotal} 条，其中 ${fbNegative} 条低分`,
        });
      } else if (fbTotal > 0) {
        items.push({
          id: `adv-${Date.now()}-2`,
          rule: "R06",
          title: `官网已有 ${fbTotal} 条用户评论`,
          action: "定期导出评论到经营复盘，把高频需求纳入工具迭代清单（评论区是免费的需求收集器）。",
          basis: `评论反馈共 ${fbTotal} 条，无低分`,
        });
      }

      // R08 GEO 获客：访问低 → 检查内容铺设
      if (weekVisits < 50 && stats) {
        items.push({
          id: `adv-${Date.now()}-3`,
          rule: "R08",
          title: "官网访问量偏低，GEO 内容铺设要加速",
          action: "用 GEO 关键词矩阵生成器补内容：先做 3 篇高搜索意图选题（对标 topPages 相关词），发布到知乎/头条/公众号，两周后再看可见性。",
          basis: `近 7 天访问 ${weekVisits} 次（<50 阈值）`,
        });
      }

      // R18 官网访问趋势指导内容方向
      if (stats && weekVisits >= 50) {
        items.push({
          id: `adv-${Date.now()}-4`,
          rule: "R18",
          title: "官网流量有基础，向「转化」发力",
          action: "在访问最高的页面添加咨询表单/微信入口，每周五复盘时对比「访问→咨询」转化率，低于 2% 就优化入口位置与话术。",
          basis: `近 7 天访问 ${weekVisits} 次，已达到起步量`,
        });
      }

      // R17 一人公司：先跑通 MVP 闭环
      items.push({
        id: `adv-${Date.now()}-5`,
        rule: "R17",
        title: "本周目标：跑通一次「内容→获客→成交」闭环",
        action: "选本周计划里最相关的 1 篇内容，发布后盯 3 天数据（阅读/咨询），有咨询立即跟进到成交；把过程写进晚上复盘。",
        basis: hasRules ? "命中规则库 R17：先跑通 MVP 闭环再扩" : "规则库读取失败，使用默认经营常识",
      });

      // R04 北极星指标
      items.push({
        id: `adv-${Date.now()}-6`,
        rule: "R04",
        title: "给本周定一个北极星指标",
        action: "本周只盯 1 个指标（建议：官网咨询数 或 有效获客数），所有行动向它对齐；周五复盘只看它变没变。",
        basis: hasRules ? "命中规则库 R04：北极星指标只有一个" : "规则库读取失败，使用默认经营常识",
      });

      setAdvice(items.slice(0, 5));
    } catch (e) {
      setError(`建议生成失败：${e instanceof Error ? e.message : String(e)}`);
      // 兜底：至少给出规则库常识建议
      setAdvice([
        {
          id: `adv-fb-${Date.now()}`,
          rule: "R17",
          title: "本周目标：跑通一次「内容→获客→成交」闭环",
          action: "选本周计划里最相关的 1 篇内容，发布后盯 3 天数据，有咨询立即跟进到成交。",
          basis: "数据接口暂不可用，使用规则库默认建议",
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAdvice();
  }, [loadAdvice]);

  // 标记完成（软状态，仅 UI 反馈）
  const markDone = (id: string) => {
    setAdvice((prev) => prev.map((a) => (a.id === id ? { ...a, done: true } : a)));
  };

  // P1-① 加载 6 个 Ontology 索引，拼成"企业地图"紧凑文本（先看地图，再给建议）
  const loadOntologyMap = useCallback(async (): Promise<string> => {
    const chunks: string[] = [];
    for (const p of ONTO_FILES) {
      try {
        const r = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`).catch(() => null);
        if (r && r.ok) {
          const d = await r.json().catch(() => null);
          if (d?.success && typeof d.content === "string") {
            const t = p.split("/").pop()?.replace("-index.md", "") || "";
            const body = d.content.replace(/```/g, "").slice(0, 320);
            chunks.push(`· ${t}: ${body.replace(/\n+/g, " ")}`);
          }
        }
      } catch { /* 单条失败不影响整体 */ }
    }
    return chunks.join("\n");
  }, []);

  // AI 深化分析（用户主动点击才调 LLM，控制 token）
  const runDeep = async () => {
    setDeepLoading(true);
    setDeepResult("");
    try {
      const statsResp = await fetch(`/stats?key=${STATS_KEY}&days=7`).catch(() => null);
      let statsBrief = "官网统计数据暂不可用";
      if (statsResp && statsResp.ok) {
        const s = await statsResp.json().catch(() => null);
        if (s) {
          const top = Array.isArray(s.topPages) ? s.topPages.slice(0, 3).map((p: any) => p?.path ?? p?.page ?? "").join("/") : "";
          statsBrief = `今日访问${s.todayVisits ?? "?"}，近7天${s.weekVisits ?? s.totalVisits ?? "?"}，热门页面：${top || "无"}`;
        }
      }
      // P1-① 拼接 Ontology「企业地图」作为提示顶层（先看地图，再给建议）
      const mapCtx = await loadOntologyMap();
      const resp = await fetch("/hermes-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content:
            "你是企业 COO。先阅读下面的【企业地图（Ontology）】掌握企业所知道的一切，再基于经营数据给出 3-5 条最该马上执行的结论（每条一句话，带明确动作；引用地图实体时写清来源类目）。\n\n" +
            (mapCtx ? `【企业地图】\n${mapCtx.slice(0, 2400)}\n\n` : "") +
            `【经营数据】${statsBrief}；\n` +
            "【当前建议】" + advice.map((a) => `[${a.rule}] ${a.title}：${a.action}`).join("；"),
        }),
      });
      const d = await resp.json().catch(() => null);
      setDeepResult(d?.reply ?? d?.content ?? "AI 深化分析未返回结果，请稍后重试");
    } catch (e) {
      setDeepResult(`AI 深化分析失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDeepLoading(false);
    }
  };

  const doneCount = advice.filter((a) => a.done).length;

  return (
    <div className="rounded-lg border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.06] to-transparent overflow-hidden">
      <div className="px-3 py-2 border-b border-white/[0.04] flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Lightbulb size={11} className="text-violet-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            AI 经营建议
          </span>
          <span className="text-[9px] font-mono text-gray-500">
            {loading ? "读取数据中…" : `已完成 ${doneCount}/${advice.length}`}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => void loadAdvice()}
            disabled={loading}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors disabled:opacity-40"
            title="重新读取数据并生成建议"
          >
            <RefreshCw size={9} className={loading ? "animate-spin" : ""} />
            刷新
          </button>
          <button
            onClick={() => void runDeep()}
            disabled={deepLoading}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-violet-300 hover:text-white hover:bg-violet-500/20 transition-colors disabled:opacity-40"
            title="调用 AI 深度分析（消耗少量 token）"
          >
            {deepLoading ? <Loader2 size={9} className="animate-spin" /> : <Sparkles size={9} />}
            AI 深化
          </button>
        </div>
      </div>

      <div className="p-3 space-y-2 max-h-[340px] overflow-y-auto">
        {error ? (
          <div className="flex items-start gap-2 text-[10px] text-amber-400/80">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {deepResult ? (
          <div className="p-2.5 rounded-md bg-violet-500/[0.08] border border-violet-500/20">
            <div className="flex items-center gap-1 mb-1.5">
              <Sparkles size={9} className="text-violet-300" />
              <span className="text-[9px] font-mono text-violet-300 uppercase tracking-wider">AI 深度建议</span>
            </div>
            <p className="text-[10px] leading-relaxed text-gray-300 whitespace-pre-wrap">{deepResult}</p>
          </div>
        ) : null}

        <AnimatePresence initial={false}>
          {advice.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`p-2.5 rounded-md border ${
                a.done
                  ? "border-emerald-500/20 bg-emerald-500/[0.05] opacity-70"
                  : "border-white/[0.06] bg-white/[0.02]"
              }`}
            >
              <div className="flex items-start gap-2">
                <span
                  className="flex-shrink-0 mt-0.5 px-1 py-0.5 rounded text-[8px] font-mono"
                  style={{ background: "rgba(167,139,250,0.15)", color: "#C4B5FD" }}
                >
                  {a.rule}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-[10.5px] font-medium ${a.done ? "line-through text-gray-500" : "text-gray-200"}`}>
                    {a.title}
                  </div>
                  <div className="text-[9.5px] text-gray-400 mt-0.5 leading-relaxed">{a.action}</div>
                  <div className="flex items-center gap-1 mt-1 text-[8.5px] font-mono text-gray-600">
                    <ArrowRight size={8} />
                    {a.basis}
                  </div>
                </div>
                <button
                  onClick={() => markDone(a.id)}
                  disabled={a.done}
                  className="flex-shrink-0 mt-0.5 p-1 rounded text-gray-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors disabled:opacity-40"
                  title={a.done ? "已完成" : "标记完成"}
                >
                  <CheckCircle2 size={11} className={a.done ? "text-emerald-400" : ""} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
